/**
 * Country-article + blog data layer.
 *
 * 2026-05-09 (perf pass — A from operator's perf menu): EN catalog is now
 * also lazy-loaded. Previously, `articles.json` was statically imported
 * → bundled into `index.js` (~1.4 MB raw / ~365 KB gzip), even for users
 * who never visited a country/blog page. Now it lives in its own chunk
 * loaded via `bootArticles()` before the router mounts. Result: index.js
 * shrinks ~60-70% (514 KB → ~140-200 KB gzip).
 *
 * Why this is safe: routes call `getArticle / getCountryArticle / get-
 * BlogArticle / listCountryArticleSlugs / listBlogSlugs / listBlog-
 * Articles` in their `render()` (synchronous). `main.ts` already awaits
 * `bootArticles()` before `Router.init()`, so by the time any route
 * renders, EN data is in memory and these reads stay synchronous.
 *
 * Architecture (from prior 2026-05-08 pass — locale-split):
 *   • EN body — lazy-loaded chunk (NEW 2026-05-09)
 *   • Non-EN locale bodies — lazy-loaded per-locale chunks via
 *     `import.meta.glob`, fetched on demand
 *   • Slug listings — derived from EN catalog (single source of truth;
 *     all locales ship the same key set)
 *
 * Routes that read the catalogs wait for them on an in-app navigation
 * (RouteDef.whenReady → articlesPending). If a render still runs first, the
 * lookups fall through to an empty EN catalog rather than throwing.
 */
import { mergeBlogPost, type Article, type ArticlesMap, type BlogArticle, type BlogPostData, type CountryArticle } from '@roamzy/shared';
import { getLocale } from '../i18n';
import type { Locale } from '../i18n/types';
import { AVAILABLE_LOCALES } from '../i18n/types';

// ─── EN catalog (lazy chunk) ───────────────────────────────────────────────
// Mutable so bootArticles() can populate it. Starts empty; consumers must
// not read from `EN` directly until after `await bootArticles()`.
let EN: ArticlesMap = {};

// `import.meta.glob` accepts a literal glob — even for a single file it
// produces a thunk that Vite splits into its own chunk. Same pattern we
// use for non-EN locales below; consistent treatment across all five.
const enLoader = import.meta.glob<{ default: ArticlesMap }>(
  '../../../data/articles.json',
);

async function loadEnArticles(): Promise<void> {
  if (Object.keys(EN).length > 0) return;
  const entries = Object.values(enLoader);
  if (entries.length === 0) return;
  const loader = entries[0]!;
  const mod = await loader();
  EN = mod.default;
  // Re-derive blogEn now that EN is populated.
  blogEn = Object.fromEntries(
    Object.entries(EN).filter(([k]) => k.startsWith('blog/')),
  );
  articlesByLocale.en = EN;
  blogByLocale.en = blogEn;
}

// EN blog content lives in articles.json, keyed by `blog/<slug>`. Filled
// by `loadEnArticles()` once the EN chunk arrives. Consumers (mainly
// `getBlogArticle`) pull from `blogByLocale[loc]` first, falling back to
// blogEn if the localized blog catalog hasn't loaded yet.
let blogEn: ArticlesMap = {};

// ─── Lazy non-EN catalogs ──────────────────────────────────────────────────
// `import.meta.glob` without `eager:true` returns thunks. Each thunk is a
// dynamic import that Vite emits as its own chunk — so each locale lives
// in its own JS file and only gets fetched when the user actually needs
// that locale. EN visitors never download RU/ES/PT/TR.
const articleLoaders = import.meta.glob<{ default: ArticlesMap }>(
  '../../../data/articles-*.json',
);
const blogLoaders = import.meta.glob<{ default: ArticlesMap }>(
  '../../../data/blog-*.json',
);

// In-memory cache of resolved catalogs. Once a locale is loaded it stays
// loaded for the rest of the session.
const articlesByLocale: Partial<Record<Locale, ArticlesMap>> = {};
const blogByLocale: Partial<Record<Locale, ArticlesMap>> = {};

/**
 * Resolve the file paths used by `import.meta.glob` for a given locale.
 * The glob returns absolute paths from the importing module; we look up
 * by suffix to keep this resilient to dir refactors.
 */
function findLoader<T>(loaders: Record<string, () => Promise<T>>, suffix: string): (() => Promise<T>) | null {
  for (const [path, loader] of Object.entries(loaders)) {
    if (path.endsWith(suffix)) return loader;
  }
  return null;
}

/**
 * Is everything a page in this locale reads already in memory: English, and
 * the locale's country and blog catalogs (for the locales that have them)?
 */
export function articlesReady(loc: Locale): boolean {
  if (Object.keys(EN).length === 0) return false;
  if (loc === 'en') return true;
  return (!!articlesByLocale[loc] || !findLoader(articleLoaders, `/articles-${loc}.json`))
    && (!!blogByLocale[loc] || !findLoader(blogLoaders, `/blog-${loc}.json`));
}

/** For RouteDef.whenReady: null when the catalogs are in, else the load to wait for. */
export function articlesPending(): Promise<void> | null {
  return articlesReady(getLocale()) ? null : ensureArticlesLoaded(getLocale());
}

/**
 * Fetch + cache the country-article catalog and (if it exists) the blog
 * catalog for a locale. EN short-circuits if already loaded. Idempotent:
 * subsequent calls return immediately for already-cached locales.
 */
export async function ensureArticlesLoaded(loc: Locale): Promise<void> {
  // EN is the source-of-truth fallback for slug listings, so always
  // ensure it's loaded first regardless of the requested locale.
  await loadEnArticles();
  if (loc === 'en') return;
  const promises: Promise<unknown>[] = [];
  if (!articlesByLocale[loc]) {
    const loader = findLoader(articleLoaders, `/articles-${loc}.json`);
    if (loader) {
      promises.push(loader().then((mod) => {
        articlesByLocale[loc] = mod.default;
      }));
    }
  }
  if (!blogByLocale[loc]) {
    const loader = findLoader(blogLoaders, `/blog-${loc}.json`);
    if (loader) {
      promises.push(loader().then((mod) => {
        blogByLocale[loc] = mod.default;
      }));
    }
  }
  if (promises.length > 0) await Promise.all(promises);
}

/**
 * Boot hook for main.ts. Loads the active locale (whatever `getLocale()`
 * returns at boot) before the router mounts so the first route render
 * has its catalog ready. Always loads EN first as the fallback.
 */
export async function bootArticles(): Promise<void> {
  await ensureArticlesLoaded(getLocale());
}

function active(): ArticlesMap {
  return articlesByLocale[getLocale()] ?? EN;
}

/**
 * Legacy export — used only for cross-references in render. Now a getter
 * so it always reflects the currently-loaded EN catalog (rather than an
 * empty snapshot taken at module-init time before lazy load completed).
 *
 * DEPRECATED for new code: prefer `listBlogArticles()` for enumeration
 * use cases. Kept for backward compat with `routes/blog-article.ts`.
 */
export const articles: ArticlesMap = new Proxy({} as ArticlesMap, {
  get(_target, key) {
    return EN[key as string];
  },
  ownKeys() {
    return Object.keys(EN);
  },
  getOwnPropertyDescriptor(_target, key) {
    if (key in EN) {
      return {
        value: EN[key as string],
        enumerable: true,
        configurable: true,
        writable: false,
      };
    }
    return undefined;
  },
  has(_target, key) {
    return key in EN;
  },
});

export function getArticle(key: string): Article | null {
  const cur = active();
  return cur[key] ?? EN[key] ?? null;
}

/**
 * Blog article reader. Lookup chain:
 *   1. data/blog-{loc}.json for the active locale (if loaded + present)
 *   2. EN blog catalog from articles.json
 */
export function getBlogArticle(slug: string): BlogArticle | null {
  const loc = getLocale();
  const key = `blog/${slug}`;
  const localized = blogByLocale[loc]?.[key];
  if (localized && localized.type === 'article') return localized;
  const en = blogEn[key];
  return en && en.type === 'article' ? en : null;
}

/**
 * Which locales a blog post is actually translated into. Reads the optional
 * `locales` array on the EN blog entry (always loaded); absent → all 5.
 * Used by the hreflang emitter so a partially-localized post never advertises
 * a locale it lacks. Mirrors backend `util/hreflang.ts` blogPresentLocales.
 */
const ALL_LOCALES: Locale[] = [...AVAILABLE_LOCALES];
export function blogLocalesFor(slug: string): Locale[] {
  const en = blogEn[`blog/${slug}`];
  const declared = en && en.type === 'article' ? en.locales : undefined;
  if (!Array.isArray(declared)) return ALL_LOCALES;
  const set = new Set(declared);
  const present = ALL_LOCALES.filter((l) => set.has(l));
  // EN is the canonical and always present even if omitted from `locales`.
  return present.includes('en') ? present : ['en', ...present];
}

export function getCountryArticle(slug: string): CountryArticle | null {
  const cur = active();
  const a = cur[`countries/${slug}`] ?? EN[`countries/${slug}`];
  return a && a.type === 'country' ? a : null;
}

// Slug listings are language-agnostic — every locale ships the same set of
// keys (193 country articles + 7 blog articles), only bodies differ. We list
// from the EN catalog as the source of truth.
export function listBlogSlugs(): string[] {
  return Object.entries(EN)
    .filter(([, a]) => a.type === 'article')
    .map(([k]) => k.replace(/^blog\//, ''));
}

export function listCountryArticleSlugs(): string[] {
  return Object.entries(EN)
    .filter(([, a]) => a.type === 'country')
    .map(([k]) => k.replace(/^countries\//, ''));
}

/**
 * A post's translation and its English original, for field-by-field merging
 * (shared mergeBlogPost), the way the server resolves it.
 */
export function getBlogPostSources(slug: string): { localized: BlogArticle | null; en: BlogArticle | null } {
  const key = `blog/${slug}`;
  const loc = getLocale();
  const localized = loc === 'en' ? null : blogByLocale[loc]?.[key];
  const en = blogEn[key];
  return {
    localized: localized && localized.type === 'article' ? localized : null,
    en: en && en.type === 'article' ? en : null,
  };
}

/**
 * Every post for the journal index, from the English master list, each field
 * from the page language's copy else English (shared mergeBlogPost), as the
 * server builds it.
 */
export function listBlogIndex(): Array<[string, BlogPostData]> {
  const loc = getLocale();
  return Object.keys(blogEn)
    .filter((k) => k.startsWith('blog/'))
    .map((k) => [k.slice('blog/'.length), mergeBlogPost(loc === 'en' ? null : blogByLocale[loc]?.[k] as BlogPostData | undefined, blogEn[k] as BlogPostData | undefined) ?? {}]);
}

/** Does the page language have its own copy of this post (the server checks its catalog the same way)? */
export function hasBlogPost(slug: string): boolean {
  const loc = getLocale();
  if (loc === 'en') return true;
  return Object.prototype.hasOwnProperty.call(blogByLocale[loc] ?? {}, `blog/${slug}`);
}

/**
 * Enumerate blog articles for cross-linking (e.g. blog-article.ts
 * "related" rail). Returns [key, article] pairs from the active locale,
 * falling back to EN. Replaces the legacy `articles` export's
 * `Object.entries()` use case.
 */
export function listBlogArticles(): Array<[string, Article]> {
  // The active locale's BLOG catalog. This read `active()`, the country
  // catalog, which holds no posts outside English: "Keep reading" was empty
  // on every ru/zh/es/pt/tr post (found 2026-09-30). Only translated posts
  // are listed, so a locale-prefixed link to any of them is a real page.
  const source = blogByLocale[getLocale()] ?? blogEn;
  return Object.entries(source).filter(([, a]) => a.type === 'article');
}
